import React from "react";
import {
  Collapse,
  Tag,
  Space,
  Typography,
  Table,
  List,
  Alert,
} from "antd";
import dayjs from "dayjs";
import { formatToINR } from "../../../helpers/utility";

const AssignedPackages = ({ assignedPackages }) => {
  const customDetailRow = (title, item, color) => (
    <List.Item
      style={{
        padding: "6px 0",
        borderBottom: "none",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography.Text
        style={{
          color: "#4A5565",
          fontSize: 14,
          fontWeight: title === "Discount" ? 500 : 400,
        }}
      >
        {title}
      </Typography.Text>
      <Typography.Text
        style={{
          color: color || "#111827",
          fontSize: 14,
          fontWeight: color ? 600 : 500,
        }}
      >
        {item}
      </Typography.Text>
    </List.Item>
  );

  const getTableData = (pkg) => {
    if (Array.isArray(pkg.package_services)) {
      return pkg.package_services.map((s, i) => ({
        key: i,
        name: s.service?.name || s.name || `Service ${i + 1}`,
        unit_price: s.price ?? s.unit_price ?? 0,
        flat_discount: s.discount ?? 0,
        final_price: s.final_price ?? (s.price ?? 0) - (s.discount ?? 0),
        qty: s.quantity ?? 1,
      }));
    }

    if (
      pkg.price_snapshot &&
      typeof pkg.price_snapshot === "object"
    ) {
      return Object.entries(pkg.price_snapshot).map(([k, v], idx) => ({
        key: idx,
        name: k,
        unit_price: v.price ?? 0,
        flat_discount: v.discount ?? 0,
        final_price: (v.price ?? 0) - (v.discount ?? 0),
        qty: v.quantity ?? 1,
      }));
    }

    return [];
  };

  const columns = [
    {
      title: "Item",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div>
          <div>{text}</div>
          <div style={{ color: "#888", fontSize: 12 }}>Qty: {record.qty}</div>
        </div>
      ),
    },
    {
      title: "Type",
      key: "type",
      render: () => "Service",
    },
    {
      title: "Price",
      dataIndex: "final_price",
      key: "final_price",
      render: (value) => (
        <div style={{ color: "#F54900", fontSize: 14, fontWeight: 600 }}>
          {formatToINR(value)}
        </div>
      ),
    },
  ];

  const getTotalPrice = (pkg) => {
    const price = Number(pkg?.price || pkg?.total_package_price || 0);
    const discount = Number(pkg?.discount || 0);
    return price - discount;
  };

  if (!assignedPackages?.length) {
    return (
      <Typography.Text type="secondary">No packages assigned yet.</Typography.Text>
    );
  }

  return (
    <>
      {assignedPackages.map((pkg, index) => {
        const tableData = getTableData(pkg);
        const isActive =
          pkg?.is_active ||
          (pkg?.status || "").toLowerCase() === "ordered";

        return (
          <Collapse
            key={pkg.id ?? index}
            expandIconPosition="right"
            defaultActiveKey={[0]}
            className="custom-collapse"
            style={{ marginTop: 10, borderRadius: 12, border: "none" }}
          >
            <Collapse.Panel
              className="white-panel-header"
              style={{
                background: "#fff",
                borderRadius: 12,
                boxShadow:
                  "0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)",
                border: "none",
              }}
              header={
                <Space size={8} wrap>
                  <Typography.Text strong style={{ fontSize: 15 }}>
                    {pkg.package?.name ||
                      pkg.package_name ||
                      `Package ${index + 1}`}
                  </Typography.Text>

                  {isActive && (
                    <Tag
                      style={{
                        color: "#16a34a",
                        background: "#DCFCE7",
                        border: "none",
                        borderRadius: 6,
                        fontWeight: 500,
                      }}
                    >
                      Active
                    </Tag>
                  )}

                  <Tag
                    style={{
                      color: "#2563EB",
                      background: "#DBEAFE",
                      border: "none",
                      borderRadius: 6,
                      fontWeight: 500,
                    }}
                  >
                    {pkg.start_date
                      ? dayjs(pkg.start_date).format("MMM DD, hh:mm A")
                      : "N/A"}
                    {pkg.end_date &&
                      ` – ${dayjs(pkg.end_date).format("MMM DD, hh:mm A")}`}
                  </Tag>

                  <Tag
                    style={{
                      color: "#F54900",
                      background: "#FFE5DA",
                      border: "none",
                      borderRadius: 6,
                      fontWeight: 500,
                    }}
                  >
                    Total : {formatToINR(getTotalPrice(pkg))}
                  </Tag>
                </Space>
              }
              key={0}
            >
              <Typography.Text
                strong
                style={{
                  fontSize: 14,
                  color: "#111827",
                  display: "block",
                  marginBottom: 10,
                }}
              >
                Services
              </Typography.Text>

              <Table
                bordered
                columns={columns}
                dataSource={tableData}
                pagination={false}
                size="small"
              />

              {Number(pkg?.discount) > 0 && (
                <List split={false} style={{ padding: "12px 4px 0" }}>
                  {customDetailRow(
                    "Discount",
                    `- ${formatToINR(pkg.discount)}`,
                    "#00A63E"
                  )}
                </List>
              )}

              <Alert
                style={{
                  marginTop: 8,
                  borderRadius: 10,
                  background: "#FFFBEB",
                  border: "1px solid #FDE68A",
                  padding: "10px 16px",
                }}
                description={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography.Text
                      strong
                      style={{ fontSize: 14, color: "#101828", fontWeight: 600 }}
                    >
                      Total Package Price
                    </Typography.Text>
                    <Typography.Text
                      style={{ color: "#F54900", fontSize: 18, fontWeight: 700 }}
                    >
                      {formatToINR(getTotalPrice(pkg))}
                    </Typography.Text>
                  </div>
                }
                type="warning"
                showIcon={false}
              />
            </Collapse.Panel>
          </Collapse>
        );
      })}
    </>
  );
};

export default AssignedPackages;
